import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import api from '../api/axiosConfig';
import { PromptContractResult, PromptDetail, PromptDryRunResult, PromptSummary } from '../types';

/**
 * Editing of the two pipeline prompts.
 *
 * <p>These decide what a doctor is told about a lesion, so the page is built around not breaking
 * them: the server refuses a save that violates the contract, every save is a new version that can
 * be rolled back, and a draft can be tried on a finished analysis before it goes live.
 */
const Prompts: React.FC = () => {
    const [prompts, setPrompts] = useState<PromptSummary[]>([]);
    const [selected, setSelected] = useState<string | null>(null);
    const [detail, setDetail] = useState<PromptDetail | null>(null);
    const [draft, setDraft] = useState('');
    const [note, setNote] = useState('');
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [contract, setContract] = useState<PromptContractResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [showBundled, setShowBundled] = useState(false);
    const [dryRunOpen, setDryRunOpen] = useState(false);
    const [analysisId, setAnalysisId] = useState('');
    const [dryRun, setDryRun] = useState<PromptDryRunResult | null>(null);

    const dirty = useMemo(
        () => detail !== null && draft !== detail.body,
        [draft, detail],
    );

    const loadList = useCallback(async () => {
        try {
            const response = await api.get<PromptSummary[]>('/admin/prompts');
            setPrompts(response.data);
            setSelected(current => current ?? response.data[0]?.key ?? null);
        } catch (e: any) {
            setError(e.response?.data?.message || 'Не удалось загрузить список промптов');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadDetail = useCallback(async (key: string) => {
        setBusy(true);
        setContract(null);
        try {
            const response = await api.get<PromptDetail>(`/admin/prompts/${key}`);
            setDetail(response.data);
            setDraft(response.data.body);
            setNote('');
        } catch (e: any) {
            setError(e.response?.data?.message || 'Не удалось загрузить промпт');
        } finally {
            setBusy(false);
        }
    }, []);

    useEffect(() => { loadList(); }, [loadList]);
    useEffect(() => { if (selected) loadDetail(selected); }, [selected, loadDetail]);

    const handleContractError = (e: any, fallback: string) => {
        const data = e.response?.data;
        if (data && Array.isArray(data.errors)) {
            setContract(data as PromptContractResult);
            setError(null);
        } else {
            setError(data?.message || fallback);
        }
    };

    const validate = async () => {
        if (!selected) return;
        setBusy(true);
        setNotice(null);
        setError(null);
        try {
            const response = await api.post<PromptContractResult>(
                `/admin/prompts/${selected}/validate`, { body: draft });
            setContract(response.data);
        } catch (e: any) {
            handleContractError(e, 'Не удалось проверить промпт');
        } finally {
            setBusy(false);
        }
    };

    const save = async () => {
        if (!selected) return;
        setBusy(true);
        setNotice(null);
        setError(null);
        setContract(null);
        try {
            const response = await api.put<PromptDetail>(
                `/admin/prompts/${selected}`, { body: draft, note: note || null });
            setDetail(response.data);
            setDraft(response.data.body);
            setNote('');
            setNotice(`Сохранено как версия ${response.data.activeVersion}. Применяется со следующего анализа.`);
            loadList();
        } catch (e: any) {
            handleContractError(e, 'Не удалось сохранить промпт');
        } finally {
            setBusy(false);
        }
    };

    const activate = async (version: number) => {
        if (!selected) return;
        setBusy(true);
        setNotice(null);
        setError(null);
        setContract(null);
        try {
            const response = await api.post<PromptDetail>(
                `/admin/prompts/${selected}/versions/${version}/activate`, {});
            setDetail(response.data);
            setDraft(response.data.body);
            setNotice(`Активна версия ${version}.`);
            loadList();
        } catch (e: any) {
            handleContractError(e, 'Не удалось откатить промпт');
        } finally {
            setBusy(false);
        }
    };

    const revert = async () => {
        if (!selected) return;
        if (!window.confirm('Вернуться к тексту из сборки? Сохранённые версии останутся и их можно будет включить снова.')) {
            return;
        }
        setBusy(true);
        setNotice(null);
        setError(null);
        setContract(null);
        try {
            const response = await api.delete<PromptDetail>(`/admin/prompts/${selected}`);
            setDetail(response.data);
            setDraft(response.data.body);
            setNotice('Используется промпт из сборки.');
            loadList();
        } catch (e: any) {
            handleContractError(e, 'Не удалось вернуть промпт из сборки');
        } finally {
            setBusy(false);
        }
    };

    const runDryRun = async () => {
        if (!selected || !analysisId.trim()) return;
        setBusy(true);
        setDryRun(null);
        setError(null);
        try {
            const response = await api.post<PromptDryRunResult>(
                `/admin/prompts/${selected}/dry-run`,
                { body: draft, analysisId: analysisId.trim() });
            setDryRun(response.data);
        } catch (e: any) {
            handleContractError(e, 'Пробный запуск не удался');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Container fluid className="py-4">
            <h4 className="mb-1">Промпты ИИ</h4>
            <div className="muted-small mb-3">
                Текст, по которому модель формирует заключение. Изменения применяются со следующего
                анализа, перезапуск сервера не нужен. Сохранение блокируется, если текст ломает
                договорённости с кодом.
            </div>

            {error && <Alert variant="danger" className="py-2">{error}</Alert>}
            {notice && <Alert variant="success" className="py-2">{notice}</Alert>}

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : (
                <Row>
                    <Col md={3}>
                        <Card className="shadow-sm mb-3">
                            <Card.Body className="p-2">
                                {prompts.map(prompt => (
                                    <div
                                        key={prompt.key}
                                        role="button"
                                        onClick={() => setSelected(prompt.key)}
                                        className={`p-2 rounded ${selected === prompt.key ? 'bg-light border' : ''}`}
                                    >
                                        <div className="fw-semibold">{prompt.title}</div>
                                        <div className="muted-small">
                                            {prompt.overridden
                                                ? <Badge bg="primary">версия {prompt.activeVersion}</Badge>
                                                : <Badge bg="secondary">из сборки</Badge>}
                                            <span className="ms-2">{Math.round(prompt.length / 1024)} КБ</span>
                                        </div>
                                        {prompt.updatedBy && (
                                            <div className="muted-small text-truncate">
                                                {prompt.updatedBy}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </Card.Body>
                        </Card>

                        {detail && (
                            <Card className="shadow-sm">
                                <Card.Body>
                                    <div className="fw-semibold mb-2">Подстановки</div>
                                    <div className="muted-small mb-2">
                                        Должны встречаться в тексте ровно эти — код подставляет только их.
                                    </div>
                                    {detail.tokens.map(token => (
                                        <Badge
                                            bg={draft.includes(`{{${token}}}`) ? 'success' : 'danger'}
                                            className="me-1 mb-1"
                                            key={token}
                                        >
                                            {`{{${token}}}`}
                                        </Badge>
                                    ))}
                                </Card.Body>
                            </Card>
                        )}
                    </Col>

                    <Col md={9}>
                        {detail && (
                            <Card className="shadow-sm">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div>
                                            <span className="fw-semibold">{detail.title}</span>
                                            {dirty && <Badge bg="warning" text="dark" className="ms-2">не сохранено</Badge>}
                                        </div>
                                        <div className="d-flex gap-2">
                                            <Button size="sm" variant="outline-secondary"
                                                    onClick={() => setShowBundled(v => !v)}>
                                                {showBundled ? 'Скрыть текст из сборки' : 'Текст из сборки'}
                                            </Button>
                                            <Button size="sm" variant="outline-secondary" disabled={busy}
                                                    onClick={validate}>
                                                Проверить
                                            </Button>
                                            <Button size="sm" variant="outline-primary" disabled={busy || !dirty}
                                                    onClick={() => setDryRunOpen(true)}>
                                                Пробный запуск
                                            </Button>
                                            <Button size="sm" variant="outline-danger" disabled={busy || !detail.overridden}
                                                    onClick={revert}>
                                                Вернуть из сборки
                                            </Button>
                                        </div>
                                    </div>

                                    {contract && (
                                        <>
                                            {contract.errors.length === 0 && (
                                                <Alert variant="success" className="py-2">
                                                    Проверка пройдена — промпт можно сохранить.
                                                </Alert>
                                            )}
                                            {contract.errors.map((message, index) => (
                                                <Alert variant="danger" className="py-2" key={`e${index}`}>
                                                    {message}
                                                </Alert>
                                            ))}
                                            {contract.warnings.map((message, index) => (
                                                <Alert variant="warning" className="py-2" key={`w${index}`}>
                                                    {message}
                                                </Alert>
                                            ))}
                                        </>
                                    )}

                                    <Form.Control
                                        as="textarea"
                                        value={draft}
                                        onChange={e => setDraft(e.target.value)}
                                        spellCheck={false}
                                        style={{ fontFamily: 'monospace', fontSize: 12, height: '55vh' }}
                                    />

                                    {showBundled && (
                                        <>
                                            <div className="muted-small mt-3 mb-1">
                                                Текст из сборки (только для чтения)
                                            </div>
                                            <Form.Control
                                                as="textarea"
                                                value={detail.bundledBody}
                                                readOnly
                                                spellCheck={false}
                                                style={{ fontFamily: 'monospace', fontSize: 12, height: '25vh' }}
                                            />
                                        </>
                                    )}

                                    <Row className="mt-3 align-items-end g-2">
                                        <Col md={8}>
                                            <Form.Label className="muted-small mb-1">
                                                Что изменили (сохранится вместе с версией)
                                            </Form.Label>
                                            <Form.Control
                                                value={note}
                                                maxLength={512}
                                                onChange={e => setNote(e.target.value)}
                                                placeholder="например: уточнил формулировку рекомендаций"
                                            />
                                        </Col>
                                        <Col md={4} className="text-end">
                                            <Button variant="primary" disabled={busy || !dirty} onClick={save}>
                                                {busy ? <Spinner animation="border" size="sm" /> : 'Сохранить и включить'}
                                            </Button>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        )}

                        {detail && detail.versions.length > 0 && (
                            <Card className="shadow-sm mt-3">
                                <Card.Body>
                                    <div className="fw-semibold mb-2">История версий</div>
                                    <Table size="sm" hover responsive className="mb-0">
                                        <thead>
                                        <tr>
                                            <th>Версия</th>
                                            <th>Когда</th>
                                            <th>Кто</th>
                                            <th>Комментарий</th>
                                            <th></th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {detail.versions.map(version => (
                                            <tr key={version.version}>
                                                <td>
                                                    {version.version}
                                                    {version.active && <Badge bg="primary" className="ms-2">активна</Badge>}
                                                </td>
                                                <td className="muted-small">
                                                    {new Date(version.createdAt).toLocaleString('ru-RU')}
                                                </td>
                                                <td className="muted-small">{version.createdBy || '—'}</td>
                                                <td className="muted-small">{version.note || '—'}</td>
                                                <td className="text-end">
                                                    <Button
                                                        size="sm"
                                                        variant="outline-secondary"
                                                        disabled={busy || version.active}
                                                        onClick={() => activate(version.version)}
                                                    >
                                                        Включить
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        )}
                    </Col>
                </Row>
            )}

            <Modal show={dryRunOpen} onHide={() => setDryRunOpen(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Пробный запуск</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="muted-small mb-2">
                        Черновик прогоняется на завершённом анализе: ничего не сохраняется и анализ не
                        меняется. Расходуется один платный запрос к модели.
                    </div>
                    <Form.Control
                        value={analysisId}
                        onChange={e => setAnalysisId(e.target.value)}
                        placeholder="analysisId"
                    />
                    <Button className="mt-2" disabled={busy || !analysisId.trim()} onClick={runDryRun}>
                        {busy ? <Spinner animation="border" size="sm" /> : 'Запустить'}
                    </Button>

                    {dryRun && (
                        <div className="mt-3">
                            <Alert variant={dryRun.accepted ? 'success' : 'danger'} className="py-2">
                                {dryRun.accepted
                                    ? `Ответ прошёл проверку (${dryRun.provider}, ${dryRun.model}).`
                                    : `Ответ не прошёл проверку: ${dryRun.problem}`}
                            </Alert>
                            {dryRun.warnings.map((message, index) => (
                                <Alert variant="warning" className="py-2" key={index}>{message}</Alert>
                            ))}
                            <div className="muted-small mb-1">Ответ модели</div>
                            <Form.Control
                                as="textarea"
                                value={dryRun.response}
                                readOnly
                                spellCheck={false}
                                style={{ fontFamily: 'monospace', fontSize: 12, height: '25vh' }}
                            />
                            <div className="muted-small mt-2 mb-1">Собранный промпт</div>
                            <Form.Control
                                as="textarea"
                                value={dryRun.prompt}
                                readOnly
                                spellCheck={false}
                                style={{ fontFamily: 'monospace', fontSize: 12, height: '20vh' }}
                            />
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </Container>
    );
};

export default Prompts;
